<template>
  <themed-modal
    name="task-form-modal"
    :title="isEditMode ? 'Edit Task' : 'Create Task'"
    size="lg"
    @close="handleClose"
  >
    <form @submit.prevent="handleSubmit" class="task-form-modal">
      <!-- Title Field -->
      <div class="task-form-modal__field">
        <label for="task-title" class="task-form-modal__label">
          Title <span class="task-form-modal__required">*</span>
        </label>
        <themed-input
          id="task-title"
          v-model="formData.title"
          type="text"
          placeholder="Enter task title"
          :error="errors.title"
          required
        />
        <span v-if="errors.title" class="task-form-modal__error">{{ errors.title }}</span>
      </div>

      <!-- Description Field -->
      <div class="task-form-modal__field">
        <label for="task-description" class="task-form-modal__label">
          Description
        </label>
        <themed-textarea
          id="task-description"
          v-model="formData.description"
          placeholder="Enter task description"
          :rows="4"
          :error="errors.description"
        />
        <span v-if="errors.description" class="task-form-modal__error">{{ errors.description }}</span>
      </div>

      <!-- Assignee Field -->
      <div class="task-form-modal__field">
        <label for="task-assignee" class="task-form-modal__label">
          Assignee
        </label>
        <vue-multiselect
          v-model="selectedAssignee"
          :options="assigneeGroups"
          group-values="items"
          group-label="group"
          :searchable="true"
          :multiple="false"
          :close-on-select="true"
          :clear-on-select="false"
          :allow-empty="true"
          :show-labels="false"
          track-by="key"
          label="label"
          placeholder="Select assignee"
          class="themed-multiselect"
          @select="handleAssigneeChange"
          @remove="handleAssigneeClear"
        />
        <span v-if="errors.assignee_id" class="task-form-modal__error">{{ errors.assignee_id }}</span>
      </div>

      <!-- Priority and Status Row -->
      <div class="task-form-modal__row">
        <div class="task-form-modal__field">
          <label for="task-priority" class="task-form-modal__label">
            Priority
          </label>
          <themed-select
            id="task-priority"
            v-model="formData.priority_id"
            :options="priorityOptions"
            placeholder="Select priority"
            :error="errors.priority_id"
          />
          <span v-if="errors.priority_id" class="task-form-modal__error">{{ errors.priority_id }}</span>
        </div>

        <div class="task-form-modal__field">
          <label for="task-status" class="task-form-modal__label">
            Status
          </label>
          <themed-select
            id="task-status"
            v-model="formData.status_id"
            :options="statusOptions"
            placeholder="Select status"
            :error="errors.status_id"
          />
          <span v-if="errors.status_id" class="task-form-modal__error">{{ errors.status_id }}</span>
        </div>
      </div>

      <!-- Start and Due Date Row -->
      <div class="task-form-modal__row">
        <div class="task-form-modal__field">
          <label for="task-start-date" class="task-form-modal__label">
            Start Date
          </label>
          <themed-input
            id="task-start-date"
            v-model="formData.start_date"
            type="date"
            :error="errors.start_date"
          />
          <span v-if="errors.start_date" class="task-form-modal__error">{{ errors.start_date }}</span>
        </div>

        <div class="task-form-modal__field">
          <label for="task-due-date" class="task-form-modal__label">
            Due Date
          </label>
          <themed-input
            id="task-due-date"
            v-model="formData.due_date"
            type="date"
            :error="errors.due_date"
          />
          <span v-if="errors.due_date" class="task-form-modal__error">{{ errors.due_date }}</span>
        </div>
      </div>

      <!-- Notes Field -->
      <div class="task-form-modal__field">
        <label for="task-notes" class="task-form-modal__label">
          Notes
        </label>
        <themed-textarea
          id="task-notes"
          v-model="formData.notes"
          placeholder="Enter any additional notes"
          :rows="3"
          :error="errors.notes"
        />
        <span v-if="errors.notes" class="task-form-modal__error">{{ errors.notes }}</span>
      </div>

      <!-- Form Actions -->
      <div class="task-form-modal__actions">
        <themed-button
          type="button"
          variant="outline"
          @click="handleClose"
          :disabled="isSubmitting"
        >
          Cancel
        </themed-button>
        <themed-button
          type="submit"
          variant="default"
          :disabled="isSubmitting"
        >
          <span v-if="isSubmitting">{{ isEditMode ? 'Updating...' : 'Creating...' }}</span>
          <span v-else>{{ isEditMode ? 'Update Task' : 'Create Task' }}</span>
        </themed-button>
      </div>
    </form>
  </themed-modal>
</template>

<script>
import VueMultiselect from 'vue-multiselect';
import ThemedModal from '../ThemedModal.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedTextarea from '../form/ThemedTextarea.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
  name: 'TaskFormModal',

  components: {
    VueMultiselect,
    ThemedModal,
    ThemedInput,
    ThemedTextarea,
    ThemedSelect,
    ThemedButton
  },

  props: {
    task: {
      type: Object,
      default: null
    },
    projectId: {
      type: Number,
      default: null
    },
    siteId: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      formData: {
        title: '',
        description: '',
        assignee_type: null,
        assignee_id: null,
        priority_id: null,
        status_id: null,
        start_date: '',
        due_date: '',
        project_id: this.projectId,
        site_id: this.siteId,
        notes: ''
      },
      selectedAssignee: null,
      assigneeGroups: [],
      priorityOptions: [],
      statusOptions: [],
      errors: {},
      isSubmitting: false
    };
  },

  computed: {
    isEditMode() {
      return !!this.task;
    }
  },

  mounted() {
    this.loadFormOptions();

    if (this.isEditMode) {
      this.populateForm();
    }

    // Show the modal
    this.$modal.show('task-form-modal');
  },

  methods: {
    async loadFormOptions() {
      try {
        const [assigneesResponse, statusesResponse, prioritiesResponse] = await Promise.all([
          axios.get('/api/tasks/assignees'),
          axios.get('/api/task-statuses'),
          axios.get('/api/task-priorities')
        ]);

        this.assigneeGroups = (assigneesResponse.data || []).map(group => ({
          group: group.group,
          items: (group.items || []).map(item => ({
            label: item.label,
            assignee_type: item.assignee_type,
            assignee_id: item.assignee_id,
            key: `${item.assignee_type}:${item.assignee_id}`
          }))
        }));

        this.statusOptions = statusesResponse.data.data.map(status => ({
          label: status.name,
          value: status.id
        }));

        this.priorityOptions = prioritiesResponse.data.data.map(priority => ({
          label: priority.name,
          value: priority.id
        }));

        // Restore selectedAssignee after groups load (edit mode)
        if (this.isEditMode && this.formData.assignee_type && this.formData.assignee_id) {
          this.restoreSelectedAssignee();
        }
      } catch (error) {
        console.error('Error loading form options:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to load form options'
        });
      }
    },

    populateForm() {
      if (this.task) {
        this.formData = {
          title: this.task.title || '',
          description: this.task.description || '',
          assignee_type: this.task.assignee_type || null,
          assignee_id: this.task.assignee_id || null,
          priority_id: this.task.priority_id,
          status_id: this.task.status_id,
          start_date: this.task.start_date || '',
          due_date: this.task.due_date || '',
          project_id: this.task.project_id || this.projectId,
          site_id: this.task.site_id || this.siteId,
          notes: this.task.notes || ''
        };

        // Set selectedAssignee from loaded task relationship
        if (this.task.assignee && this.task.assignee_type && this.task.assignee_id) {
          this.selectedAssignee = {
            label: this.task.assignee.name,
            assignee_type: this.task.assignee_type,
            assignee_id: this.task.assignee_id,
            key: `${this.task.assignee_type}:${this.task.assignee_id}`
          };
        }
      }
    },

    restoreSelectedAssignee() {
      const targetKey = `${this.formData.assignee_type}:${this.formData.assignee_id}`;
      for (const group of this.assigneeGroups) {
        const found = group.items.find(item => item.key === targetKey);
        if (found) {
          this.selectedAssignee = found;
          break;
        }
      }
    },

    handleAssigneeChange(selected) {
      this.formData.assignee_type = selected.assignee_type;
      this.formData.assignee_id = selected.assignee_id;
    },

    handleAssigneeClear() {
      this.formData.assignee_type = null;
      this.formData.assignee_id = null;
    },

    async handleSubmit() {
      this.errors = {};
      this.isSubmitting = true;

      try {
        let response;

        if (this.isEditMode) {
          response = await axios.put(`/api/tasks/${this.task.id}`, this.formData);
          this.$emit('task-updated', response.data);
        } else {
          response = await axios.post('/api/tasks', this.formData);
          this.$emit('task-created', response.data);
        }

        this.handleClose();
      } catch (error) {
        console.error('Error submitting task:', error);

        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
        } else {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Failed to save task'
          });
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    handleClose() {
      this.$modal.hide('task-form-modal');
      this.$emit('close');
    }
  }
};
</script>
